import { useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';

interface CancelOrderDialogProps {
  onClose: () => void;
  onConfirm: (note: string) => void;
  isLoading?: boolean;
}

export function CancelOrderDialog({
  onClose,
  onConfirm,
  isLoading = false,
}: CancelOrderDialogProps) {
  const { t } = useTranslation('order');
  const [note, setNote] = useState('');

  return (
    <div className="bg-neut-bg-alt rounded-lg max-w-md w-full p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-error-100 rounded-lg">
          <CircleAlert
            className="text-error-600"
            size={24}
            aria-hidden="true"
          />
        </div>
        <h3 className="text-xl text-neut-prim font-medium">
          {t('order.cancel_order_confirm_title')}
        </h3>
      </div>

      <p className="text-neut-sec mb-4 font-light text-sm">
        {t('order.cancel_order_confirm_body')}
      </p>

      <div className="space-y-4">
        <div>
          <label
            htmlFor="cancel-note"
            className="block text-sm font-medium text-neut-700 mb-1"
          >
            {t('order.cancel_order_note_label')}
          </label>
          <textarea
            id="cancel-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={isLoading}
            placeholder={t('order.cancel_order_note_placeholder')}
            className="w-full px-3 py-2 border border-neut-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-error-500 font-normal"
          />
        </div>

        <div className="flex gap-3">
          <Button
            onClick={onClose}
            disabled={isLoading}
            variant="accent"
            size="sm"
          >
            {t('order.cancel_order_keep')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => onConfirm(note.trim())}
            disabled={isLoading || !note.trim()}
          >
            {t('order.cancel_order')}
          </Button>
        </div>
      </div>
    </div>
  );
}
